import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Building2 } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { CustomerSetting, CustomerTaxZone, InvoiceDetailLevel } from '@/types/api'
import { INVOICE_DETAIL_LEVELS, TAX_ZONES } from '@/types/api'

export default function CustomersPage() {
  const queryClient = useQueryClient()

  const { data: customers = [], isLoading, isError } = useQuery({
    queryKey: ['customers'],
    queryFn:  () => api.get<CustomerSetting[]>('/customers'),
    staleTime: 5 * 60 * 1000,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: { invoiceDetailLevel?: InvoiceDetailLevel; taxZone?: CustomerTaxZone | null } }) =>
      api.put(`/customers/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  })

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Settings</div>
          <h1 className="page-title">Customers</h1>
          <div className="page-subtitle">Configure invoice detail level and tax zone per customer</div>
        </div>
      </div>

      {isLoading ? (
        <div className="muted" style={{ textAlign: 'center', paddingTop: 32, fontSize: 13 }}>Loading…</div>
      ) : isError ? (
        <div className="muted" style={{ textAlign: 'center', paddingTop: 32, fontSize: 13 }}>Failed to load customers — check your connection and try refreshing.</div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Fortnox #</th>
                <th style={{ width: 260 }}>Default invoice detail level</th>
                <th style={{ width: 200 }} title="Drives the revenue-account matrix lookup at invoice generation (WI-11973)">Tax zone</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Building2 size={13} style={{ color: 'var(--ns-text-muted)', flexShrink: 0 }} />
                      <span style={{ fontWeight: 500 }}>{c.name}</span>
                    </div>
                  </td>
                  <td className="mono muted" style={{ fontSize: 12 }}>
                    {c.fortnoxCustomerNumber ?? <span style={{ fontStyle: 'italic' }}>—</span>}
                  </td>
                  <td>
                    <select
                      className="input"
                      style={{ width: '100%', fontSize: 13 }}
                      value={c.invoiceDetailLevel}
                      onChange={e =>
                        updateMutation.mutate({
                          id: c.id,
                          body: { invoiceDetailLevel: e.target.value as InvoiceDetailLevel },
                        })
                      }
                    >
                      {INVOICE_DETAIL_LEVELS.map(l => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <select
                        className="input"
                        style={{ flex: 1, fontSize: 13 }}
                        value={c.taxZone ?? ''}
                        onChange={e =>
                          updateMutation.mutate({
                            id: c.id,
                            body: { taxZone: (e.target.value || null) as CustomerTaxZone | null },
                          })
                        }
                      >
                        <option value="">— not set —</option>
                        {TAX_ZONES.map(z => (
                          <option key={z.value} value={z.value}>{z.label}</option>
                        ))}
                      </select>
                      {c.taxZone === null && (
                        <span title="Invoice generation will fail until this customer has a tax zone set.">
                          <AlertTriangle size={14} style={{ color: 'var(--ns-warn)' }} />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {customers.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                    No customers yet — sync from Fortnox to populate.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
